import { action } from "@ember/object"
import { inject as service } from "@ember/service"
import Component from "@glimmer/component"
import type { Course } from "@shared/models/course"
import type { CourseDateCombined } from "@shared/models/course-date-combined"
import type { Planbook } from "@shared/models/planbook"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type { ModelsNeededForFirstEdit } from "cc-frontend/services/lesson-editor"
import { formatCourseTimePart, parseCourseTimePart } from "cc-frontend/utils/course/times"
import { isEqual } from "lodash-es"
import { tracked } from "tracked-built-ins"
import styles from "./change-time-body.module.css"

interface Sig {
  Args: {
    modelsNeededForFirstEdit: ModelsNeededForFirstEdit
    courseDate: CourseDateCombined
    course: Course
    planbook: Planbook
    closeMenu: () => void
  }
}

export default class ChangeTimeBodyComponent extends Component<Sig> {
  @service declare lessonEditor: LessonEditor

  readonly styles = styles

  @tracked startTime = 8 * 60
  @tracked endTime = 9 * 60

  constructor(owner: unknown, args: Sig["Args"]) {
    super(owner, args)

    const time = this.args.courseDate.timeCustom ?? this.args.courseDate.timeDefault
    if (time !== null) {
      this.startTime = time.start
      this.endTime = time.end
    }
  }

  get timeParts(): { startStr: string; endStr: string } {
    return {
      startStr: formatCourseTimePart(this.startTime),
      endStr: formatCourseTimePart(this.endTime),
    }
  }

  @action
  setStartTime(startTimeInputStr: string) {
    const start = parseCourseTimePart(startTimeInputStr)
    if (start === null) return
    this.startTime = start
  }

  @action
  setEndTime(endTimeInputStr: string) {
    const end = parseCourseTimePart(endTimeInputStr, this.startTime)
    if (end === null) return
    this.endTime = end
  }

  @action
  submit() {
    this.args.closeMenu()

    // We enforce start <= end when setting end but not when setting start.
    if (this.startTime > this.endTime) this.startTime = this.endTime

    // If they entered the default time, assume they want to reset it.
    const enteredTime = { start: this.startTime, end: this.endTime }
    const time = isEqual(enteredTime, this.args.courseDate.timeDefault) ? null : enteredTime

    this.lessonEditor.setCustomTime(
      this.args.modelsNeededForFirstEdit,
      this.args.course,
      this.args.courseDate.dateString,
      this.args.planbook.id,
      time
    )
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::Menus::LessonMenu::ChangeTimeBody": ChangeTimeBodyComponent
  }
}
